import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, ShieldCheck, AlertTriangle, XCircle } from "lucide-react";
import SignaturePad from "../shared/SignaturePad";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = { ok: "bg-emerald-500", warning: "bg-amber-500", fail: "bg-red-500" };
const STATUS_LABELS = { ok: "OK", warning: "Merknad", fail: "Avvist" };
const OVERALL_COLORS = { approved: "bg-emerald-500", conditional: "bg-amber-500", rejected: "bg-red-500" };
const OVERALL_LABELS = { approved: "Godkjent", conditional: "Godkjent m/anmerkning", rejected: "Ikke godkjent" };
const OVERALL_ICONS = { approved: ShieldCheck, conditional: AlertTriangle, rejected: XCircle };

export default function KontrollSign() {
  const { signToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notPresent, setNotPresent] = useState(false);
  const [signature, setSignature] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/kontroll/sign/${signToken}`);
        setData(data);
        setEmail(data.customer_email || "");
      } catch (e) {
        toast.error(e.response?.data?.detail || "Link invalid");
      }
      setLoading(false);
    })();
  }, [signToken]);

  const submit = async () => {
    if (!notPresent && !signature) { toast.error("Signer eller kryss av"); return; }
    if (!firstName.trim() || !lastName.trim()) { toast.error("Navn er obligatorisk"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/kontroll/sign/${signToken}`, {
        customer_email: email, customer_first_name: firstName, customer_last_name: lastName,
        customer_company: company, customer_phone: phone,
        customer_not_present: notPresent, signature_data_url: notPresent ? null : signature,
      });
      setDone(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Feil"); }
    setSubmitting(false);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-cyan-500" /></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500">Link invalid eller allerede signert</div>;

  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-cyan-50 to-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center mx-auto mb-5 shadow-lg">
            <CheckCircle2 className="w-12 h-12 text-white" strokeWidth={2.5} style={{ color: "#fff" }} />
          </div>
          <h1 className="text-3xl font-black text-slate-900 mb-2">Takk!</h1>
          <p className="text-slate-600 mb-6">Kontrollen er signert og en kopi er sendt på e-post.</p>
          {data.code && (
            <div className="bg-slate-900 rounded-xl p-4 shadow-md">
              <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-0.5">Kontrollnummer</div>
              <div className="text-4xl font-black text-cyan-400 tracking-tight font-mono">{data.code}</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  const overall = data.overall_status || "approved";
  const OIcon = OVERALL_ICONS[overall];

  return (
    <div className="light-theme min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 pb-16 text-slate-900">
      <div className="sticky top-0 z-40 bg-slate-900 text-white px-4 py-3 shadow-md">
        <div className="max-w-4xl mx-auto">
          <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">Doserne Stillas AS</div>
          <h1 className="text-base font-bold" style={{ color: "#fff" }}>Kontrollrapport {data.code}</h1>
        </div>
      </div>
      <div className="max-w-4xl mx-auto p-4 space-y-4">
        <div className={`${OVERALL_COLORS[overall]} rounded-xl p-4 shadow-md flex items-center gap-3`} style={{ color: "#fff" }}>
          <OIcon className="w-6 h-6" style={{ color: "#fff" }} />
          <div>
            <div className="text-[10px] uppercase tracking-widest opacity-90 font-semibold">Overordnet status</div>
            <div className="font-bold" style={{ color: "#fff" }}>{OVERALL_LABELS[overall]}</div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div><div className="text-[10px] uppercase text-slate-500 font-semibold">Dato</div><div className="font-semibold">{data.inspection_date}</div></div>
          <div><div className="text-[10px] uppercase text-slate-500 font-semibold">Inspektør</div><div className="font-semibold">{data.inspector_name}</div></div>
          <div><div className="text-[10px] uppercase text-slate-500 font-semibold">Adresse</div><div className="font-semibold">{data.address || "—"}</div></div>
          <div><div className="text-[10px] uppercase text-slate-500 font-semibold">Kunde</div><div className="font-semibold">{data.customer || "—"}</div></div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6">
          <h2 className="text-lg font-bold mb-3">Stillaser ({data.scaffolds?.length || 0})</h2>
          <div className="space-y-3">
            {data.scaffolds?.map((s, i) => (
              <div key={i} className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-bold">#{i + 1} {s.name}</div>
                  <span className={`${STATUS_COLORS[s.status]} px-2 py-0.5 rounded-full text-xs font-semibold`} style={{ color: "#fff" }}>{STATUS_LABELS[s.status]}</span>
                </div>
                <div className="text-xs text-slate-600 mb-2">
                  H×L×B: {s.height || "-"}×{s.length || "-"}×{s.width || "-"} m — Kapasitet: {s.capacity || "-"} kg/m² — Forankr: {s.number_of_anchors || "-"}
                </div>
                {s.notes && <div className="text-xs text-slate-500 italic">&quot;{s.notes}&quot;</div>}
                {s.photos?.length > 0 && <div className="flex gap-1 mt-2">{s.photos.slice(0, 6).map((p, i2) => <img key={i2} src={p} alt="" className="w-16 h-16 object-cover rounded border border-slate-300" />)}</div>}
              </div>
            ))}
          </div>
        </div>

        {data.general_notes && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="text-xs uppercase tracking-widest text-amber-800 font-bold mb-1">Merknader</div>
            <p className="text-sm text-amber-900">{data.general_notes}</p>
          </div>
        )}

        {/* Client sign block */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6 space-y-3">
          <h2 className="text-lg font-bold">Kundens bekreftelse</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Fornavn *" className="px-3 py-2 border border-slate-300 rounded-md text-slate-900 placeholder:text-slate-400" data-testid="sign-firstname" />
            <input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Etternavn *" className="px-3 py-2 border border-slate-300 rounded-md text-slate-900 placeholder:text-slate-400" data-testid="sign-lastname" />
            <input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Firma" className="px-3 py-2 border border-slate-300 rounded-md text-slate-900 placeholder:text-slate-400" />
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Telefon" className="px-3 py-2 border border-slate-300 rounded-md text-slate-900 placeholder:text-slate-400" />
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-post (kopi)" className="sm:col-span-2 px-3 py-2 border border-slate-300 rounded-md text-slate-900 placeholder:text-slate-400" data-testid="sign-email" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={notPresent} onChange={(e) => setNotPresent(e.target.checked)} data-testid="sign-not-present" />
            <span className="text-sm text-slate-700">Kunde ikke tilstede for signering</span>
          </label>
          {!notPresent && (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Signatur *</label>
              <SignaturePad value={signature} onChange={setSignature} label="Signer her" />
            </div>
          )}
          <button onClick={submit} disabled={submitting}
            className="w-full py-3 bg-cyan-500 hover:bg-cyan-600 disabled:bg-slate-400 text-white rounded-md font-bold shadow-md flex items-center justify-center gap-2"
            style={{ color: "#fff" }} data-testid="sign-submit">
            {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Sender...</> : "Godkjenn og signer"}
          </button>
        </div>
      </div>
    </div>
  );
}
